import {
  RewardScheduleContentUpdateRequest,
  RewardTypeSummary,
  RewardPostWinConfigText,
  RewardScheduleWithGame,
  S2WGame,
  S2WGameUpdateRequest,
  S2WTheme,
  PrizeRevealMethod,
} from '@Generated/back-office-client/src'
import { S2W_THEMES, supportsDoubleWheel } from './types'

export type MetadataRow = { key: string; value: string }

export type ScheduleDetailsFormData = {
  promoText: string[]
  supportLink: string
  rulesLink: string
}

export type PrizeFormData = {
  id: string
  rewardName: string
  header: string
  description: string
  disclaimer: string
  callToAction: string
  // Ordered rows so the key/value editor can hold blanks and duplicates while the user types
  metadata: MetadataRow[]
}

// Keyed by field path, e.g. 'promoText.0' or 'metadata.1.key'
export type FormErrors = Record<string, string>

const toScheduleDetailsForm = (schedule: RewardScheduleWithGame): ScheduleDetailsFormData => ({
  promoText: [...schedule.promoText],
  supportLink: schedule.supportLink ?? '',
  rulesLink: schedule.rulesLink ?? '',
})

const toPrizeForm = (reward: RewardTypeSummary): PrizeFormData => ({
  id: reward.id,
  rewardName: reward.rewardName,
  header: reward.postWinConfig.header,
  description: reward.postWinConfig.description,
  disclaimer: reward.postWinConfig.disclaimer ?? '',
  callToAction: reward.postWinConfig.callToAction ?? '',
  metadata: Object.entries(reward.postWinConfig.ctaMetadata ?? {}).map(([key, value]) => ({
    key,
    value,
  })),
})

const blankToNull = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  return trimmed
}

const toPostWinConfig = (prize: PrizeFormData): RewardPostWinConfigText => {
  const config: RewardPostWinConfigText = { header: prize.header, description: prize.description }
  // The edit replaces the whole post-win config, so an existing disclaimer must be sent back to be kept
  if (prize.disclaimer.trim()) {
    config.disclaimer = prize.disclaimer
  }
  if (prize.callToAction.trim()) {
    config.callToAction = prize.callToAction
  }
  if (prize.metadata.length > 0) {
    config.ctaMetadata = Object.fromEntries(
      prize.metadata.map(({ key, value }) => [key.trim(), value]),
    )
  }
  return config
}

// Schedule-level edit: no rewards listed, so every prize is left unchanged
const toScheduleDetailsUpdate = (
  form: ScheduleDetailsFormData,
): RewardScheduleContentUpdateRequest => ({
  promoText: form.promoText.filter((line) => line.trim()),
  supportLink: blankToNull(form.supportLink),
  rulesLink: blankToNull(form.rulesLink),
  rewards: [],
})

// Single-prize edit: promo text and links are omitted, so the API leaves them unchanged
const toPrizeUpdate = (prize: PrizeFormData): RewardScheduleContentUpdateRequest => ({
  rewards: [{ id: prize.id, rewardName: prize.rewardName, postWinConfig: toPostWinConfig(prize) }],
})

const tooLong = (limit: number) => `Must be ${limit} characters or fewer`

// Both validators mirror the server's rules so problems show before saving; the server stays the authority
// Links are shown to players as clickable links, so only http(s) URLs are allowed (the server enforces the same)
const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

const validateScheduleDetails = (form: ScheduleDetailsFormData, game: S2WGame): FormErrors => {
  const errors: FormErrors = {}
  form.promoText.forEach((line, index) => {
    if (line.length > game.promoTextLimit) {
      errors[`promoText.${index}`] = tooLong(game.promoTextLimit)
    }
  })
  const links = { supportLink: form.supportLink, rulesLink: form.rulesLink }
  Object.entries(links).forEach(([field, value]) => {
    if (value.trim() && !isHttpUrl(value.trim())) {
      errors[field] = 'Must be an http or https link'
    }
  })
  return errors
}

const validatePrize = (prize: PrizeFormData, game: S2WGame): FormErrors => {
  const errors: FormErrors = {}
  if (!prize.rewardName.trim()) {
    errors.rewardName = 'Required'
  } else if (prize.rewardName.length > game.wheelTextLimit) {
    errors.rewardName = tooLong(game.wheelTextLimit)
  }
  if (!prize.header.trim()) {
    errors.header = 'Required'
  }
  if (!prize.description.trim()) {
    errors.description = 'Required'
  }

  const seenKeys = new Set<string>()
  prize.metadata.forEach((row, rowIndex) => {
    const key = row.key.trim()
    if (!key) {
      errors[`metadata.${rowIndex}.key`] = 'Required'
    } else if (seenKeys.has(key)) {
      errors[`metadata.${rowIndex}.key`] = 'Duplicate key'
    }
    seenKeys.add(key)
  })
  return errors
}

// Numbers stay strings while editing so a half-typed value doesn't jump
export type GameFormData = {
  name: string
  pointCost: string
  // Its own flag, set only by the "No daily limit" checkbox: deriving it from a blank field would turn clearing the
  // field to retype it into "unlimited"
  noDailyLimit: boolean
  dailySpinLimit: string
  promoTextLimit: string
  wheelTextLimit: string
  // '' only when the stored theme isn't one the server accepts; one must be picked to save
  theme: S2WTheme | ''
  prizeRevealMethod: PrizeRevealMethod
}

const toGameForm = (game: S2WGame): GameFormData => {
  let theme: S2WTheme | '' = ''
  const knownTheme = S2W_THEMES.find((candidate) => candidate === game.theme)
  if (knownTheme) {
    theme = knownTheme
  }
  let dailySpinLimit = ''
  if (game.dailySpinLimit) {
    dailySpinLimit = String(game.dailySpinLimit)
  }
  // The edit always saves a reveal method, even for a game imported without one: double wheel (the client's default)
  // unless the theme only supports a single wheel
  let prizeRevealMethod = game.prizeRevealMethod ?? PrizeRevealMethod.DOUBLE_WHEEL_PRIZE_REVEAL
  if (!game.prizeRevealMethod && !supportsDoubleWheel(theme)) {
    prizeRevealMethod = PrizeRevealMethod.SINGLE_WHEEL_PRIZE_REVEAL
  }
  return {
    name: game.name,
    pointCost: String(game.pointCost),
    noDailyLimit: !game.dailySpinLimit,
    dailySpinLimit,
    promoTextLimit: String(game.promoTextLimit),
    wheelTextLimit: String(game.wheelTextLimit),
    theme,
    prizeRevealMethod,
  }
}

const WHOLE_NUMBER = /^\d+$/
// The columns are Postgres integers; the server rejects anything larger
const MAX_WHOLE_NUMBER = 2147483647

const wholeNumberError = (value: string, minimum: number): string | undefined => {
  const trimmed = value.trim()
  if (!trimmed) {
    return 'Required'
  }
  if (!WHOLE_NUMBER.test(trimmed) || Number(trimmed) < minimum) {
    return `Must be a whole number of ${minimum} or more`
  }
  if (Number(trimmed) > MAX_WHOLE_NUMBER) {
    return 'Too large'
  }
  return undefined
}

// Mirrors the server's field rules and its theme/reveal pairing. Existing schedule text is checked by the server only,
// and its message shows as the save error.
const validateGame = (form: GameFormData): FormErrors => {
  const errors: FormErrors = {}
  if (!form.name.trim()) {
    errors.name = 'Required'
  }
  const numberFields = {
    pointCost: { value: form.pointCost, minimum: 0 },
    promoTextLimit: { value: form.promoTextLimit, minimum: 1 },
    wheelTextLimit: { value: form.wheelTextLimit, minimum: 1 },
  }
  Object.entries(numberFields).forEach(([field, { value, minimum }]) => {
    const error = wholeNumberError(value, minimum)
    if (error) {
      errors[field] = error
    }
  })
  if (!form.noDailyLimit) {
    const error = wholeNumberError(form.dailySpinLimit, 1)
    if (error) {
      errors.dailySpinLimit = error
    }
  }
  if (!form.theme) {
    errors.theme = 'Required'
  }
  // The sidebar disables this pairing, but a game saved before the rule existed can still have it
  if (
    form.prizeRevealMethod === PrizeRevealMethod.DOUBLE_WHEEL_PRIZE_REVEAL &&
    !supportsDoubleWheel(form.theme)
  ) {
    errors.prizeRevealMethod = 'This theme only supports a single wheel'
  }
  return errors
}

// Only called once validateGame returns no errors
const toGameUpdate = (form: GameFormData): S2WGameUpdateRequest => {
  let dailySpinLimit: number | null = null
  if (!form.noDailyLimit) {
    dailySpinLimit = Number(form.dailySpinLimit.trim())
  }
  return {
    name: form.name.trim(),
    pointCost: Number(form.pointCost.trim()),
    dailySpinLimit,
    promoTextLimit: Number(form.promoTextLimit.trim()),
    wheelTextLimit: Number(form.wheelTextLimit.trim()),
    // validateGame rejects a blank theme, so one has been picked by now
    theme: form.theme as S2WTheme,
    prizeRevealMethod: form.prizeRevealMethod,
  }
}

export const PrizeScheduleForm = {
  toScheduleDetailsForm,
  toPrizeForm,
  toScheduleDetailsUpdate,
  toPrizeUpdate,
  validateScheduleDetails,
  validatePrize,
  toGameForm,
  validateGame,
  toGameUpdate,
}
